"use client";

import Link from "next/link";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { staggerContainer, fadeInUp } from "@/lib/animations";

export default function TermsAndConditions() {
	return (
		<div className="flex min-h-screen flex-col bg-background py-20 md:py-32">
			<div className="w-full px-6 md:px-12">
				<div className="mb-12">
					<Link
						href="/"
						className="group inline-flex items-center font-syne font-bold uppercase text-muted-foreground transition-colors hover:text-primary"
					>
						<ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1" />
						Back to Home
					</Link>
				</div>

				<motion.div
					variants={staggerContainer}
					initial="hidden"
					animate="show"
					className="mx-auto max-w-4xl"
				>
					<motion.div variants={fadeInUp}>
						<h1 className="mb-12 font-syne text-5xl font-bold uppercase tracking-tighter md:text-7xl">
							Terms & <span className="text-primary">Conditions</span>
						</h1>
					</motion.div>

					<motion.div variants={fadeInUp} className="space-y-12 font-inter text-lg leading-relaxed text-muted-foreground">
						<p className="text-sm font-bold uppercase tracking-widest text-primary">
							Last Updated: December 3, 2025
						</p>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								1. Introduction and Acceptance
							</h2>
							<p>
								Welcome to TheHighrays (&quot;Company,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;). These Terms and
								Conditions (&quot;Terms&quot;) govern your access to and use of our website at thehighrays.com,
								including all associated subdomains, as well as our services, products, and platforms
								operated under TheHighrays, TheHighraysYT, and Elec3D brands.
							</p>
							<p className="mt-4">
								By accessing our website, subscribing to our newsletter, submitting a contact form,
								purchasing our services, or otherwise engaging with our platforms, you acknowledge that
								you have read, understood, and agree to be bound by these Terms. If you do not agree to
								these Terms, you must immediately discontinue use of our services.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								2. Description of Services
							</h2>
							<p className="mb-4">
								TheHighrays operates multiple service lines and brands, including but not limited to:
							</p>

							<h3 className="mt-6 mb-3 font-syne text-xl font-bold text-foreground">
								2.1 TheHighraysYT - YouTube Growth Services
							</h3>
							<ul className="list-disc space-y-2 pl-6 marker:text-primary">
								<li>YouTube channel promotion and audience growth strategies</li>
								<li>Video views, subscriber acquisition, and watch time optimization</li>
								<li>Content strategy consulting and viral marketing campaigns</li>
								<li>YouTube advertising management and campaign optimization</li>
								<li>Analytics reporting and performance tracking</li>
							</ul>

							<h3 className="mt-6 mb-3 font-syne text-xl font-bold text-foreground">
								2.2 Elec3D - Technology and Fabrication Services
							</h3>
							<ul className="list-disc space-y-2 pl-6 marker:text-primary">
								<li>3D printing and additive manufacturing services</li>
								<li>Custom drone design, assembly, and consultation</li>
								<li>Robotics prototyping and development</li>
								<li>Artistic and collectible product creation (anime, sci-fi themed)</li>
								<li>Custom fabrication projects and technical consulting</li>
							</ul>

							<h3 className="mt-6 mb-3 font-syne text-xl font-bold text-foreground">
								2.3 Digital Marketing and Content Services
							</h3>
							<ul className="list-disc space-y-2 pl-6 marker:text-primary">
								<li>Brand identity development and digital strategy</li>
								<li>Content creation and creative direction</li>
								<li>Social media management and community building</li>
								<li>Website development and digital presence optimization</li>
							</ul>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								3. YouTube Promotion Services - Specific Terms
							</h2>
							<p className="mb-4">
								By using TheHighraysYT services, you additionally agree to the following:
							</p>
							<ul className="list-disc space-y-2 pl-6 marker:text-primary">
								<li>
									<strong className="text-foreground">Platform Compliance:</strong> Our services are designed
									to comply with YouTube&apos;s Terms of Service and Community Guidelines. However, you
									acknowledge that YouTube&apos;s policies may change, and we cannot guarantee continued
									compliance with future policy updates.
								</li>
								<li>
									<strong className="text-foreground">No Guarantees:</strong> While we strive to deliver
									results, we cannot guarantee specific view counts, subscriber numbers, watch time hours,
									or monetization eligibility. Results vary based on content quality, niche, and market conditions.
								</li>
								<li>
									<strong className="text-foreground">Account Ownership:</strong> You must own or have
									authorized access to the YouTube channel(s) for which you are purchasing services.
									We are not responsible for any disputes regarding channel ownership.
								</li>
								<li>
									<strong className="text-foreground">Content Responsibility:</strong> You are solely
									responsible for ensuring your content does not violate YouTube policies, copyright laws,
									or any applicable regulations. We reserve the right to refuse service for content that
									we deem inappropriate, illegal, or harmful.
								</li>
								<li>
									<strong className="text-foreground">Retention Rates:</strong> Subscriber and view retention
									rates are subject to platform algorithms and user behavior. We do not guarantee long-term
									retention of any metrics delivered.
								</li>
							</ul>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								4. Elec3D Products and Fabrication - Specific Terms
							</h2>
							<p className="mb-4">
								By ordering products or services from Elec3D, you additionally agree to the following:
							</p>
							<ul className="list-disc space-y-2 pl-6 marker:text-primary">
								<li>
									<strong className="text-foreground">Custom Orders:</strong> Custom fabrication projects
									are non-refundable once production has commenced. Design approvals are final.
								</li>
								<li>
									<strong className="text-foreground">Product Variations:</strong> 3D printed and handcrafted
									items may have minor variations in color, texture, or finish. These variations are
									inherent to the manufacturing process and are not considered defects.
								</li>
								<li>
									<strong className="text-foreground">Drone and Robotics Products:</strong> Any autonomous
									or remote-controlled devices are sold for lawful purposes only. You are responsible for
									complying with all local, state, and national regulations regarding drone operation,
									registration, and use.
								</li>
								<li>
									<strong className="text-foreground">Safety and Liability:</strong> You assume full
									responsibility for the safe operation of any drone, robotic, or mechanical products
									purchased from Elec3D. We are not liable for any injury, damage, or legal consequences
									arising from product use.
								</li>
								<li>
									<strong className="text-foreground">Intellectual Property:</strong> Custom designs created
									by Elec3D remain our intellectual property unless explicitly transferred in writing.
									Client-provided designs remain the property of the client.
								</li>
							</ul>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								5. User Accounts and Registration
							</h2>
							<p>
								If you create an account or register for any of our services, you agree to:
							</p>
							<ul className="mt-4 list-disc space-y-2 pl-6 marker:text-primary">
								<li>Provide accurate, current, and complete information during registration</li>
								<li>Maintain and promptly update your account information</li>
								<li>Maintain the security of your password and account credentials</li>
								<li>Accept responsibility for all activities that occur under your account</li>
								<li>Notify us immediately of any unauthorized access or security breach</li>
							</ul>
							<p className="mt-4">
								We reserve the right to suspend or terminate accounts that violate these Terms, engage
								in fraudulent activity, or are used for illegal purposes. We may also refuse service
								to anyone at our sole discretion.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								6. Newsletter and Communications
							</h2>
							<p>
								By subscribing to our newsletter or providing your email through our contact forms, you consent to receive:
							</p>
							<ul className="mt-4 list-disc space-y-2 pl-6 marker:text-primary">
								<li>Marketing communications about our services and products</li>
								<li>Updates about new features, promotions, and company news</li>
								<li>Educational content related to YouTube growth, digital marketing, and technology</li>
								<li>Transactional emails regarding your orders or service inquiries</li>
							</ul>
							<p className="mt-4">
								You may unsubscribe from marketing communications at any time by clicking the unsubscribe
								link in any email or by contacting us directly. Transactional emails related to active
								orders or services are not optional and will continue until your business relationship
								with us concludes.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								7. Intellectual Property Rights
							</h2>
							<p>
								All content on this website, including but not limited to text, graphics, logos, images,
								audio clips, video clips, digital downloads, data compilations, and software, is the
								property of TheHighrays or its content suppliers and is protected by international
								copyright and intellectual property laws.
							</p>
							<p className="mt-4 mb-2 font-bold text-foreground">
								You are prohibited from:
							</p>
							<ul className="list-disc space-y-2 pl-6 marker:text-primary">
								<li>Reproducing, duplicating, or copying content for commercial purposes without written permission</li>
								<li>Modifying, distributing, or publicly displaying our content without authorization</li>
								<li>Using our trademarks, logos, or branding in any manner that suggests endorsement or affiliation</li>
								<li>Reverse engineering, decompiling, or attempting to extract source code from our software or platforms</li>
								<li>Scraping, data mining, or using automated tools to collect data from our websites</li>
							</ul>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								8. Payments, Pricing, and Billing
							</h2>
							<p>
								All prices are listed in the currency specified at the time of purchase. We accept
								payment methods as indicated during the checkout process.
							</p>
							<ul className="mt-4 list-disc space-y-2 pl-6 marker:text-primary">
								<li>
									<strong className="text-foreground">Payment Timing:</strong> Payment for services is
									due in advance unless otherwise agreed in writing. For custom projects, we may require
									a deposit before commencing work.
								</li>
								<li>
									<strong className="text-foreground">Price Changes:</strong> We reserve the right to
									modify prices at any time. Price changes will not affect orders already confirmed.
								</li>
								<li>
									<strong className="text-foreground">Taxes:</strong> Prices may not include applicable
									taxes. You are responsible for any taxes, duties, or fees imposed by your jurisdiction.
								</li>
								<li>
									<strong className="text-foreground">Failed Payments:</strong> If a payment fails or is
									reversed, we may suspend services until the balance is paid in full.
								</li>
							</ul>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								9. Refund and Cancellation Policy
							</h2>
							<p className="mb-4">
								Our refund policy varies by service type:
							</p>
							<ul className="list-disc space-y-2 pl-6 marker:text-primary">
								<li>
									<strong className="text-foreground">YouTube Promotion Services:</strong> Once a campaign
									has been initiated, refunds are not available for delivered metrics. For undelivered portions,
									we may offer partial refunds or credits at our discretion.
								</li>
								<li>
									<strong className="text-foreground">Custom Fabrication (Elec3D):</strong> Custom orders
									are non-refundable once production begins. Ready-made products may be returned within
									14 days if unused and in original condition, subject to a restocking fee.
								</li>
								<li>
									<strong className="text-foreground">Consulting Services:</strong> Consulting fees are
									non-refundable after the session has been conducted.
								</li>
								<li>
									<strong className="text-foreground">Subscription Services:</strong> Subscriptions may be
									cancelled at any time, but refunds for partial periods are not provided.
								</li>
							</ul>
							<p className="mt-4">
								To request a refund or cancellation, contact us at info@thehighrays.com with your order
								details. All refund requests are reviewed on a case-by-case basis.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								10. Service Delivery and Timelines
							</h2>
							<p>
								We strive to deliver services within the timeframes specified at the time of purchase.
								However, delivery times are estimates and not guarantees. Factors outside our control,
								including platform algorithm changes, third-party dependencies, supply chain issues,
								or force majeure events, may affect delivery.
							</p>
							<p className="mt-4">
								We will communicate proactively about any significant delays and work with you to find
								satisfactory solutions.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								11. Prohibited Uses
							</h2>
							<p className="mb-4">
								You agree not to use our services or website for any purpose that is:
							</p>
							<ul className="list-disc space-y-2 pl-6 marker:text-primary">
								<li>Illegal, fraudulent, or harmful to others</li>
								<li>Violates any applicable laws or regulations</li>
								<li>Infringes on intellectual property rights of third parties</li>
								<li>Promotes hate speech, violence, or discrimination</li>
								<li>Involves the transmission of malware, spam, or malicious code</li>
								<li>Attempts to gain unauthorized access to our systems or user accounts</li>
								<li>Interferes with the proper functioning of our website or services</li>
								<li>Involves content that is sexually explicit, exploitative, or harmful to minors</li>
							</ul>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								12. Disclaimer of Warranties
							</h2>
							<p>
								Our services and website are provided &quot;as is&quot; and &quot;as available&quot; without warranties
								of any kind, either express or implied. To the fullest extent permitted by law, we disclaim
								all warranties, including but not limited to:
							</p>
							<ul className="mt-4 list-disc space-y-2 pl-6 marker:text-primary">
								<li>Implied warranties of merchantability and fitness for a particular purpose</li>
								<li>Warranties regarding the accuracy, reliability, or completeness of content</li>
								<li>Warranties that services will be uninterrupted, secure, or error-free</li>
								<li>Warranties regarding the results obtained from using our services</li>
							</ul>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								13. Limitation of Liability
							</h2>
							<p>
								To the maximum extent permitted by applicable law, TheHighrays, its officers, directors,
								employees, agents, and affiliates shall not be liable for any indirect, incidental, special,
								consequential, or punitive damages, including but not limited to:
							</p>
							<ul className="mt-4 list-disc space-y-2 pl-6 marker:text-primary">
								<li>Loss of profits, revenue, or business opportunities</li>
								<li>Loss of data or content</li>
								<li>Loss of goodwill or reputation</li>
								<li>Channel suspension, demonetization, or penalties imposed by third-party platforms</li>
								<li>Damage to property or personal injury related to product use</li>
							</ul>
							<p className="mt-4">
								Our total liability for any claim arising from these Terms or your use of our services
								shall not exceed the amount you paid to us in the twelve (12) months preceding the claim.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								14. Indemnification
							</h2>
							<p>
								You agree to indemnify, defend, and hold harmless TheHighrays, its officers, directors,
								employees, agents, and affiliates from and against any and all claims, liabilities,
								damages, losses, costs, and expenses (including reasonable legal fees) arising from:
							</p>
							<ul className="mt-4 list-disc space-y-2 pl-6 marker:text-primary">
								<li>Your violation of these Terms</li>
								<li>Your use of our services or products</li>
								<li>Content you provide or submit through our platforms</li>
								<li>Your violation of any third-party rights</li>
								<li>Your violation of any applicable laws or regulations</li>
							</ul>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								15. Third-Party Links and Services
							</h2>
							<p>
								Our website and services may contain links to third-party websites, platforms, or services
								that are not owned or controlled by TheHighrays. We have no control over, and assume no
								responsibility for, the content, privacy policies, or practices of any third-party sites
								or services.
							</p>
							<p className="mt-4">
								Your use of third-party services, including YouTube, payment processors, and other platforms,
								is subject to their respective terms and conditions.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								16. Dispute Resolution
							</h2>
							<p>
								In the event of any dispute arising from these Terms or your use of our services, you
								agree to first attempt to resolve the matter informally by contacting us at info@thehighrays.com.
								We will work in good faith to reach a mutually satisfactory resolution within 30 days.
							</p>
							<p className="mt-4">
								If informal resolution is unsuccessful, any dispute shall be resolved through binding
								arbitration in accordance with the Arbitration and Conciliation Act, 1996 of India.
								The arbitration shall be conducted in New Delhi, India, in the English language.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								17. Governing Law and Jurisdiction
							</h2>
							<p>
								These Terms and Conditions are governed by and construed in accordance with the laws of
								India, without regard to its conflict of law principles. For any matters not subject
								to arbitration, you consent to the exclusive jurisdiction of the courts located in
								New Delhi, India.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								18. Severability
							</h2>
							<p>
								If any provision of these Terms is found to be invalid, illegal, or unenforceable by a
								court of competent jurisdiction, such invalidity shall not affect the validity of the
								remaining provisions, which shall continue in full force and effect.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								19. Entire Agreement
							</h2>
							<p>
								These Terms, together with our Privacy Policy and any service-specific agreements,
								constitute the entire agreement between you and TheHighrays regarding the use of our
								services and supersede all prior agreements, representations, and understandings.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								20. Changes to Terms
							</h2>
							<p>
								We reserve the right to modify or replace these Terms at any time. Material changes will
								be notified via email to registered users or through a prominent notice on our website.
								Your continued use of our services after any changes constitutes acceptance of the
								revised Terms.
							</p>
							<p className="mt-4">
								We encourage you to review these Terms periodically for updates.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								21. Contact Information
							</h2>
							<p className="mb-4">
								For any questions, concerns, or requests regarding these Terms and Conditions, please contact us:
							</p>
							<div className="border-l-2 border-primary pl-6">
								<p className="font-bold text-foreground">TheHighrays</p>
								<p className="mt-2">
									<strong className="text-foreground">Email:</strong>{" "}
									info@thehighrays.com
								</p>
								<p>
									<strong className="text-foreground">Phone:</strong> +91 8273262260
								</p>
								<p>
									<strong className="text-foreground">Address:</strong> New Delhi, Delhi, India
								</p>
								<p className="mt-4 text-sm">
									Business Hours: Monday - Friday, 10:00 AM - 6:00 PM IST
								</p>
							</div>
						</section>
					</motion.div>
				</motion.div>
			</div>
		</div>
	);
}
